import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import H3 from '@base/H3/H3';
import P1 from '@base/P1/P1';
import Link from '@base/Link/Link';
import InfiniteList from '@comp/InfiniteList/InfiniteList';
import TestSeriesCard from '@comp/TestSeriesCard/TestSeriesCard';
import Dropdown from '@comp/Dropdown/Dropdown';
import useWindowSize from '@hooks/useWindowSize';
import useRefreshDataOnGloUpdate from '@hooks/useRefreshDataOnGloUpdate';
import { getImage } from '@utils/imageHelper';
import pushRoute from '@utils/pushRoute';
import { Spacings, breakpoints, Fonts } from '@styles/index';
import useTestSeries from '@api/hooks/goal/test-series/useTestSeries';
import useIsRecordedGoal from '@hooks/useIsRecordedGoal';
import { getValidTestType } from '../utils';
import { OPTIONS } from '../constants';
import Header from './Common/Header';
import Loader from './Common/Loader';

const NoCourses = styled(H3)`
  margin-top: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_5B};
  color: var(--color-text-primary);
`;

const linkStyle = `margin-bottom: ${Spacings.SPACING_6B};`;

const DropdownWrapper = styled.div`
  grid-column: span 18;
  display: grid;
  grid-template-columns: repeat(18, 1fr);
  grid-column-gap: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_6B};

  @media only screen and (max-width: ${breakpoints.mobile}) {
    grid-column: span 11;
    grid-column-gap: ${Spacings.SPACING_2B};
    grid-template-columns: repeat(10, 1fr);
  }
`;

const StyledDropdown = styled(Dropdown)`
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin-bottom: ${Spacings.SPACING_6B};
    grid-column: span 11;
    height: ${Spacings.SPACING_12B};
  }
`;

const Empty = styled.div`
  grid-column: span 18;
  text-align: center;
  margin-top: ${Spacings.SPACING_24B};
`;

const EmptyImage = styled.img`
  user-drag: none;
  user-select: none;
`;

const BoldP1 = styled(P1)`
  font-weight: ${Fonts.BOLD};
`;

const SeeAllButton = styled.div`
  height: 40px;
  width: 100%;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--color-i-white);
`;

const TestListingLanding = ({
  dataAnalytics,
  showAll,
  maxLimit,
  customPageDetails
}) => {
  const router = useRouter();
  const {
    query: { goalUID, goalSlug, 'test-type': testType },
    pathname
  } = router;
  const isRecordedGoal = useIsRecordedGoal(goalUID);
  const {
    testSeriesData,
    mutate: mutateTestSeries,
    isLoading,
    setSize,
    size
  } = useTestSeries(goalUID, testType || 1);

  const { isMobile } = useWindowSize();

  const initalType = getValidTestType(testType);
  const [testSeriesType, setTestSeriesType] = useState(initalType);

  useEffect(() => {
    setTestSeriesType(getValidTestType(testType));
  }, [testType]);

  const optionSelect = (option) => {
    mutateTestSeries();
    setTestSeriesType(option.value);

    pushRoute(
      {
        pathname,
        query: {
          goalUID,
          goalSlug,
          'test-type': option.value
        }
      },
      undefined,
      { shallow: true }
    );
  };

  const getSelectedOption = () =>
    OPTIONS.find((option) => option.value === testSeriesType);

  // const data = testSeriesData[getTestSeriesFeedKey(goalUID, testSeriesType)];
  const testSeriesList = testSeriesData?.results?.slice(0, maxLimit);

  const isEmpty = !isLoading && !testSeriesList.length;
  const isLessThenLimit = isEmpty ? true : testSeriesData?.length <= maxLimit;

  const loadMore = () => setSize(size + 1);

  useRefreshDataOnGloUpdate(loadMore);

  const renderTestSeries = (test, index) => (
    <TestSeriesCard
      test={test}
      key={test.uid}
      linkStyle={linkStyle}
      index={index}
      dataAnalytics={dataAnalytics}
    />
  );

  const renderContent = () => {
    const emptyTitle = isRecordedGoal
      ? 'Tests will be available soon'
      : 'No test series found';
    const emptyDescription = isRecordedGoal
      ? 'We are working on it'
      : 'The educator hasn’t added any courses yet. Try changing the filters';
    if (isEmpty) {
      return (
        <Empty>
          <EmptyImage src={getImage('empty.svg')} alt="empty-content" />
          <NoCourses>{emptyTitle}</NoCourses>
          <P1 color="var(--color-text-secondary)">{emptyDescription}</P1>
        </Empty>
      );
    }

    if (showAll) {
      return (
        <InfiniteList
          data={testSeriesData}
          loadMore={loadMore}
          _renderItem={renderTestSeries}
          gridColumn="5/15"
        />
      );
    }

    if (isLoading) {
      return <Loader />;
    }

    return testSeriesList?.map((test, index) => (
      <TestSeriesCard
        test={test}
        key={test.uid}
        linkStyle={linkStyle}
        index={index}
        dataAnalytics={dataAnalytics}
      />
    ));
  };

  const seeAllHref =
    isLessThenLimit || showAll
      ? null
      : `/goal/${goalSlug}/${goalUID}/test-series/all?test-type=${testSeriesType}`;

  return (
    <>
      <Header
        label={customPageDetails?.testSeriesBlockTitle ?? 'Test Series'}
        description={customPageDetails?.testSeriesBlockDescription}
        seeAllHref={seeAllHref}
        isMobile={isMobile}
      />
      <DropdownWrapper>
        <StyledDropdown
          options={OPTIONS}
          onOptionClick={optionSelect}
          selectedOption={getSelectedOption()}
          menuWidth={isMobile ? '100%' : '176px'}
        />
      </DropdownWrapper>

      {renderContent()}
      {seeAllHref && isMobile && (
        <SeeAllButton>
          <Link href={seeAllHref} shallow>
            <BoldP1 color="var(--color-i-green)">See All</BoldP1>
          </Link>
        </SeeAllButton>
      )}
    </>
  );
};

export default TestListingLanding;

TestListingLanding.propTypes = {
  maxLimit: PropTypes.number,
  showAll: PropTypes.bool,
  dataAnalytics: PropTypes.string
};

TestListingLanding.defaultProps = {
  maxLimit: 6,
  showAll: true,
  dataAnalytics: ''
};
